/**
 * Does a meeting report cover its recording (#1289)? The report refers to the transcript with times from the start of
 * the recording (mm:ss or h:mm:ss) and ranges (78:00-118:00). The check compares them with the linked recording parts:
 * references close together cover the stretch between them, a passage the report says it left out counts as accounted,
 * and what remains are the gaps. Deterministic, no judgement of the report itself.
 *
 * shortcut: a clock time in the text (om 14:30) reads as a recording time, so it can hide a gap; good enough for a warning.
 */

/** References this close together (or to the start or end of a part) cover the stretch between them. */
export const JOIN_MS = 5 * 60_000;
/** Below this percentage, or with a gap longer than GAP_MS, the report gets the warning report-coverage-gap. */
export const MIN_PERCENT = 80;
export const GAP_MS = 10 * 60_000;

export type TimeRef = { startMs: number; endMs: number; omitted: boolean };
export type CoveragePart = { recordingId: string; startMs: number; endMs: number };
export type Coverage = { percent: number; coveredMs: number; totalMs: number; omittedMs: number; gaps: CoveragePart[] };

const T = String.raw`(?<![\d:])\d{1,3}(?::\d{2}){1,2}(?![\d:])`;
const RANGE = new RegExp(`(${T})(?:\\s*(?:-|–|—|tot|to|until)\\s*(${T}))?`, 'g');
const OMITTED = /weggelaten|overgeslagen|niet (?:in het )?verslag|omitted|skipped|left out|not reported/i;

function ms(t: string): number | null {
  const n = t.split(':').map(Number);
  const [h, m, s] = n.length === 3 ? n : [0, ...n];
  if (m! > 59 && n.length === 3) return null;
  if (s! > 59) return null;
  return ((h! * 60 + m!) * 60 + s!) * 1000;
}

/** The time references in a report, as points (start = end) or ranges; on a line that names an omission, omitted. */
export function timeRefs(report: string): TimeRef[] {
  return report.split('\n').flatMap((line) => {
    const omitted = OMITTED.test(line);
    return [...line.matchAll(RANGE)].flatMap((m) => {
      const a = ms(m[1]!);
      if (a === null) return [];
      const b = m[2] ? ms(m[2]) : null;
      return [{ startMs: a, endMs: b !== null && b >= a ? b : a, omitted }];
    });
  });
}

/** The coverage of the linked parts by the report; null without parts (nothing to compare). */
export function reportCoverage(report: string, parts: CoveragePart[]): Coverage | null {
  if (!parts.length) return null;
  const refs = timeRefs(report);
  const gaps: CoveragePart[] = [];
  let totalMs = 0;
  let omittedMs = 0;
  for (const p of parts) {
    totalMs += p.endMs - p.startMs;
    const inPart = refs
      .filter((r) => r.endMs >= p.startMs && r.startMs <= p.endMs)
      .map((r) => ({ ...r, startMs: Math.max(r.startMs, p.startMs), endMs: Math.min(r.endMs, p.endMs) }))
      .sort((a, b) => a.startMs - b.startMs);
    let omittedTo = p.startMs; // union of the omitted ranges, so an overlap counts once
    for (const r of inPart.filter((x) => x.omitted)) {
      omittedMs += Math.max(0, r.endMs - Math.max(r.startMs, omittedTo));
      omittedTo = Math.max(omittedTo, r.endMs);
    }
    let at = p.startMs;
    for (const r of inPart) {
      if (r.startMs - at > JOIN_MS) gaps.push({ recordingId: p.recordingId, startMs: at, endMs: r.startMs });
      at = Math.max(at, r.endMs);
    }
    if (p.endMs - at > JOIN_MS) gaps.push({ recordingId: p.recordingId, startMs: at, endMs: p.endMs });
  }
  const coveredMs = totalMs - gaps.reduce((sum, g) => sum + g.endMs - g.startMs, 0);
  return { percent: totalMs ? Math.floor((coveredMs / totalMs) * 100) : 100, coveredMs, totalMs, omittedMs, gaps };
}

/** Below 80 percent, or a gap over ten minutes. */
export const coverageGap = (c: Coverage) => c.percent < MIN_PERCENT || c.gaps.some((g) => g.endMs - g.startMs > GAP_MS);
